// File: src/components/EditProductDialog.tsx
// FINAL CLEAN VERSION. Uses central API and inherits styles from the theme.

import React, {useEffect, useState} from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';
import {Product} from '../../types';
import {updateProduct} from '../../services/api';
import ImageUpload from '../ImageUpload';

interface EditProductDialogProps {
    open: boolean;
    product: Product | null;
    onClose: () => void;
    onSuccess: () => void;
}

const EditProductDialog: React.FC<EditProductDialogProps> = ({open, product, onClose, onSuccess}) => {
    const {t} = useTranslation();
    const [formData, setFormData] = useState<Partial<Product>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (product) {
            setFormData(product);
            setError(null);
        }
    }, [product]);

    const handleInputChange = (field: keyof Omit<Product, 'id' | 'created_at'>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({...formData, [field]: event.target.value});
    };

    const handleImagesChange = (newImages: string[]) => {
        setFormData({...formData, images: newImages});
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!product || !formData.name?.trim()) {
            setError(t('products.edit.name_required'));
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await updateProduct(product.id, formData);
            onSuccess();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('products.edit.unknown_error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {t('products.edit.title')}
                <IconButton
                    aria-label={t('products.edit.close')}
                    onClick={onClose}
                    sx={{position: 'absolute', right: 8, top: 8}}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                )}
                <TextField
                    autoFocus
                    required
                    margin="dense"
                    label={t('products.edit.product_name')}
                    fullWidth
                    value={formData.name || ''}
                    onChange={handleInputChange('name')}
                />
                <TextField
                    margin="dense"
                    label={t('products.edit.brand')}
                    fullWidth
                    value={formData.brand || ''}
                    onChange={handleInputChange('brand')}
                />
                <TextField
                    margin="dense"
                    label={t('products.edit.description')}
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.description || ''}
                    onChange={handleInputChange('description')}
                />
                <ImageUpload
                    images={formData.images || []}
                    onImagesChange={handleImagesChange}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {t('products.edit.cancel')}
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? <CircularProgress size={24}/> : t('products.edit.update_product')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditProductDialog;
